import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { testimonialsApi, type Testimonial } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

export type { Testimonial };

export function useTestimonials(activeOnly = false) {
  const queryClient = useQueryClient();
  const { toast }   = useToast();

  const { data: testimonials, isLoading } = useQuery({
    queryKey: ['testimonials', activeOnly],
    queryFn:  () => testimonialsApi.list(activeOnly),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) =>
      testimonialsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      toast({ title: "Sucesso", description: "Depoimento criado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro", description: `Erro ao criar depoimento: ${e.message}`, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Testimonial> }) =>
      testimonialsApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      toast({ title: "Sucesso", description: "Depoimento atualizado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro", description: `Erro ao atualizar depoimento: ${e.message}`, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => testimonialsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      toast({ title: "Sucesso", description: "Depoimento deletado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro", description: `Erro ao deletar depoimento: ${e.message}`, variant: "destructive" }),
  });

  return {
    testimonials: Array.isArray(testimonials) ? testimonials : [],
    isLoading,
    createTestimonial: createMutation.mutate,
    updateTestimonial: updateMutation.mutate,
    deleteTestimonial: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
